import { type Dispatch, useMemo, useReducer } from 'react';
import { Configure, Index } from 'react-instantsearch';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { getAlgoliaGiftBoxSearchOptions, getAlgoliaIndex } from '../../../shared/algoliaHelpers';
import { type ProductSearchItem } from '../../../shared/types';
import { useRelatedProducts } from '../../hooks/useRelatedProducts';
import {
    searchReducer,
    type SearchReducerAction,
    type SearchReducerState,
} from '../../reducers/searchReducer';
import { DistanceBoundaryInKm } from '../../types/enums/distanceBoundaryInKm';
import { type ProductDetailContext } from '../../types/types';
import { AlgoliaWrapper } from '../AlgoliaWrapper/AlgoliaWrapper';
import { LoadMoreButton } from '../LoadMoreButton';
import { NoGiftBoxProductsSearchResult } from '../NoResult/NoGiftBoxProductsSearchResult';
import { NoResultsBoundary } from '../NoResult/NoResultsBoundary';
import { Filter } from './Filter';
import { GiftBoxRelatedProductWrapper } from './GiftBoxRelatedProductWrapper';

type GiftBoxRelatedProductsProps = {
    readonly productId: string;
    readonly inPreview: boolean;
};

type GiftBoxRelatedProductsContentProps = {
    readonly dispatchSearch: Dispatch<SearchReducerAction>;
    readonly searchState: SearchReducerState;
};

const GiftBoxRelatedProductsContent = ({
    dispatchSearch,
    searchState,
}: GiftBoxRelatedProductsContentProps): JSX.Element | null => {
    const { hasNoResults, hasRelatedProducts, isLastPage, products, showMore, status } =
        useRelatedProducts();

    if (!hasRelatedProducts) {
        return null;
    }

    return (
        <>
            <Filter
                dispatchSearch={dispatchSearch}
                locationName={searchState.name}
                distanceValue={searchState.distance}
            />
            <NoResultsBoundary
                hasNoResults={hasNoResults}
                fallback={<NoGiftBoxProductsSearchResult />}
            >
                <>
                    <div className="grid">
                        {products.map((product: ProductSearchItem, index: number) => {
                            return (
                                <div
                                    className="g-col-12 g-col-xs-6 mb-sm-4x mb-2x"
                                    key={product.productId}
                                >
                                    <GiftBoxRelatedProductWrapper
                                        manufacturerSku={product.manufacturerSku}
                                        name={product.title}
                                        images={product.images}
                                        position={index}
                                        closestLocation={product.closestLocation}
                                        locations={product.locations}
                                        classifications={product.classifications}
                                        isInPreview={product.inPreview}
                                        rating={product.rating}
                                        lazyLoad
                                        trackingPath={product.trackingPath}
                                        price={product.price}
                                        originalPrice={product.originalPrice}
                                        productType={product.productType}
                                        minParticipants={product.minParticipants}
                                        maxParticipants={product.maxParticipants}
                                        productInformation={product.productInformation}
                                    />
                                </div>
                            );
                        })}
                    </div>

                    {!isLastPage && (
                        <LoadMoreButton onClick={showMore} disabled={status === 'loading'} />
                    )}
                </>
            </NoResultsBoundary>
        </>
    );
};

const GiftBoxRelatedProducts = ({
    inPreview,
    productId,
}: GiftBoxRelatedProductsProps): JSX.Element => {
    const { algoliaConfig, locale, tenant } = useFragmentContext<ProductDetailContext>();
    const [searchState, dispatchSearch] = useReducer(searchReducer, {
        distance: DistanceBoundaryInKm.DEFAULT,
    });

    const searchOptions = useMemo(
        () => getAlgoliaGiftBoxSearchOptions(productId, inPreview, searchState),
        [inPreview, productId, searchState],
    );

    const indexName = useMemo(
        () =>
            getAlgoliaIndex({
                locale,
                tenant,
                environment: algoliaConfig.environment,
            }),
        [locale, tenant, algoliaConfig.environment],
    );

    return (
        <AlgoliaWrapper>
            <Index indexName={indexName}>
                <Configure {...searchOptions} />
                <GiftBoxRelatedProductsContent
                    dispatchSearch={dispatchSearch}
                    searchState={searchState}
                />
            </Index>
        </AlgoliaWrapper>
    );
};

export { GiftBoxRelatedProducts };

export default GiftBoxRelatedProducts;
